import apiService from '../utils/apiservice'

// Vera (DISTRIBUTION PLATFORM) members use the veranotification service
export const isVeraMember = () => localStorage.getItem('SOURCE') === 'DISTRIBUTION PLATFORM'

// Extra query param identifying Vera members ('' for UHF members)
export const veraPlatformParam = () => (isVeraMember() ? '&platform=' + encodeURIComponent('DISTRIBUTION PLATFORM') : '')

// e.g. https://dev.fabric.carynhealth.com/api/v8/Twillio/ -> https://dev.fabric.carynhealth.com/veranotification/api/v8/Twillio/
const VERA_NOTIFICATION_URL = (process.env.REACT_APP_NEW_BASE_URL_NOTIFICATION || '').replace('/api/', '/veranotification/api/')

export const getVeraNotificationDetails = (page, size = 10) => {
  let mail = localStorage.getItem('userMail')
  let URL = VERA_NOTIFICATION_URL + 'getNotificationDetails/' + mail
  if (page !== undefined && page !== null) {
    URL += '?page=' + page + '&size=' + size
  }
  return apiService
    .get(URL)
    .then(response => {
      return response
    })
    .catch()
}

// Transaction components come either flat ([{ planName, amount }, ...]) or nested per product
// ([{ core, appFee, addons, others }]; core's includedComponents are not shown) - return a flat [{ planName, amount }] list.
const flattenVeraComponents = components =>
  (components || []).flatMap(component => {
    if (component?.amount != null && component?.planName) return [component]
    return [component?.core, component?.appFee, ...(component?.addons || []), ...(component?.others || [])].filter(
      item => item?.amount != null && item?.planName
    )
  })

// My Payments for Vera members: the member's transaction history from distributionPaymentService
// (filtered by the member's sourceid), mapped to the fields TransactionDataTable reads.
// Resolves in the same { data: { response: [] } } shape as getTransactionData().
export const getVeraTransactionData = () => {
  // sourceid is stored as e.g. "2000000007-UHF"; the API wants only the numeric part
  const sourceId = (localStorage.getItem('sourceid') || '').split('-')[0]
  const columns = [
    'transactionId',
    'firstName',
    'lastName',
    'agentId',
    'createdDate',
    'paymentMode',
    'transactionAmount',
    'authorizationMessage',
    'affiliationId',
    'memberId',
    'planName',
    'source'
  ]
  const params = new URLSearchParams()
  columns.forEach((col, i) => {
    params.append('columns[' + i + '].data', col)
    params.append('columns[' + i + '].name', '')
    params.append('columns[' + i + '].searchable', 'true')
    params.append('columns[' + i + '].orderable', 'true')
    params.append('columns[' + i + '].search.regex', 'false')
    params.append('columns[' + i + '].search.value', col === 'source' ? sourceId : '')
  })
  params.append('draw', '1')
  params.append('start', '0')
  params.append('length', '1000')
  params.append('order[0].column', '4')
  params.append('order[0].dir', 'desc')
  params.append('search.regex', 'false')
  params.append('search.value', '')

  const URL =
    process.env.REACT_APP_NEW_BASE_URL_AGENT_DETAIL + 'distributionPaymentService/api/v1/transactions/transactionHistoryForAgency'

  return apiService
    .post(URL, params)
    .then(response => {
      const rows = (response?.data?.data || []).map(txn => ({
        transactionId: txn.transactionId,
        createdDate: txn.createdDate,
        paymentMethod: txn.type,
        transactionAmount: txn.transactionAmount,
        authorizationMessage: txn.authorizationMessage,
        refundAmount: txn.refundAmount,
        type: txn.type,
        // Shown in the row's expand section instead of Monthly Share / UHF Membership Fee
        components: flattenVeraComponents(txn.components)
      }))
      return { data: { response: rows } }
    })
    // e.g. "Member not found" - show the page's "not available" message instead of failing
    .catch(() => ({ data: { response: [] } }))
}

// Member enrollment info (gives subId): Vera members use veraenroll with the numeric source
// ("2000000007-UHF" -> 2000000007); everyone else keeps the UHF v3.0 enrollment API
export const getEnrollMemberInfoByIdUrl = sourceCode =>
  isVeraMember()
    ? process.env.REACT_APP_NEW_BASE_URL_AGENT_DETAIL + 'veraenroll/api/v1/enrollment/getEnrollMemberInfoById/' + String(sourceCode).split('-')[0]
    : process.env.REACT_APP_BASE_URL + 'v3.0/enrollment/getEnrollMemberInfoById/' + sourceCode

// Save billing address: returns [url, payload] for apiService/axios .post(...).
// Vera members use veraenroll, which also expects the address under the plain field names.
export const addMemberAddressRequest = obj =>
  isVeraMember()
    ? [
        process.env.REACT_APP_NEW_BASE_URL_AGENT_DETAIL + 'veraenroll/api/v1/setupfamily/addMemberAddress',
        {
          ...obj,
          street: obj.mailAddress1,
          country: obj.mailCountry,
          city: obj.mailCity,
          state: obj.mailState,
          postalCode: obj.mailPostalCode,
          address1: ''
        }
      ]
    : [process.env.REACT_APP_BASE_URL + 'v3.0/setupfamily/addMemberAddress', obj]
